import { Loader } from '@mantine/core'
import { DASHBOARD_DATE_PICKER_HEIGHT } from 'App/dashboards/_components/DashboardDateRangePicker'
import { DownloadIcon } from 'App/dashboards/_components/DownloadIcon'
import { useDashboardHeader } from 'App/dashboards/_utils/dashboardHeaderContext'
import { StickyOffsetContext, useStickyOffset } from 'App/dashboards/_utils/stickyOffsetContext'
import { translate } from 'i18n/i18n'
import type { ReactNode } from 'react'
import { colors, dashboardColors } from 'settings/colors'

// Header sizing. The chart widget's header carries the date picker, so its height is
// control + vertical padding + bottom border. We floor every header at that so a title-only header
// (e.g. Historical Trap Counts) keeps the same top/bottom rhythm.
const HEADER_PADDING_Y = 12
const HEADER_PADDING_X = 16
const HEADER_MIN_HEIGHT = DASHBOARD_DATE_PICKER_HEIGHT + HEADER_PADDING_Y * 2 + 1 // +1 for the bottom border
// Scrolled state: the card header tightens to 48, drops its underline, and gives up the gap beneath
// it — the same trade the page header makes, so the two collapse together rather than in sequence.
const HEADER_MIN_HEIGHT_COMPACT = 48
const BODY_PADDING = 16

export const WidgetCard = ({
  title,
  count,
  onDownload,
  headerActions,
  loading = false,
  fillHeight = false,
  children,
}: {
  title: string
  count?: number
  onDownload?: () => void
  // Fill the remaining height of the dashboard column instead of sizing to content, so the widget
  // can scroll internally. Off by default — cards normally grow with their content.
  fillHeight?: boolean
  headerActions?: ReactNode
  loading?: boolean
  children: ReactNode
}) => {
  // Only set on dashboards whose shell opts in; elsewhere this is null and the card never compacts.
  const compact = useDashboardHeader()?.compact ?? false
  // Where this card's header should pin, and what the next level down should clear.
  const stickyOffset = useStickyOffset()
  const headerHeight = compact ? HEADER_MIN_HEIGHT_COMPACT : HEADER_MIN_HEIGHT
  // A card whose widget owns its own scroll region doesn't pin — nothing scrolls past it.
  const pinHeader = stickyOffset !== null && !fillHeight

  // Surfaces the download only once the header's own button has scrolled away, and only while the card
  // is still on screen — otherwise it would linger over whatever the user scrolled on to.

  return (
    <div
      css={{
        background: colors.white,
        border: `1px solid ${colors.grey200}`,
        borderRadius: 8,
        // `overflow: hidden` clips the rounded corners, but it also makes this the scrollport for any
        // sticky descendant — which silently stops the header, property banners and column headers
        // from pinning. When they need to pin, the radius moves onto the children instead.
        ...(pinHeader ? {} : { overflow: 'hidden' }),
        ...(fillHeight ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 } : {}),
      }}
    >
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: `${HEADER_PADDING_Y}px ${HEADER_PADDING_X}px`,
          // border-box so the min-height is the outer header height regardless of the page's box model.
          boxSizing: 'border-box',
          minHeight: headerHeight,
          transition: 'min-height 150ms ease',
          ...(pinHeader
            ? {
                position: 'sticky',
                top: stickyOffset,
                // Above the loading mask (4). When this header pins, the card body travels up
                // underneath it, so the mask's `inset: 0` of the body DOES reach the header's painted
                // box — at 3 the mask washed the pinned title, count and download control in 65% white.
                zIndex: 5,
                background: colors.white,
                borderRadius: '8px 8px 0 0',
              }
            : {}),
          // Wrap on narrower (iPad) widths so the controls drop below the title instead of overflowing.
          flexWrap: 'wrap',
        }}
      >
        <h2 css={{ fontSize: 18, fontWeight: 600, margin: 0, color: colors.midnight }}>{title}</h2>

        {count !== undefined && (
          <span css={{ fontSize: 12, fontWeight: 600, color: dashboardColors.mutedText }}>
            {count === 1
              ? translate.phrases.banyanApp('{{count}} Property', { count })
              : translate.phrases.banyanApp('{{count}} Properties', { count })}
          </span>
        )}

        {/* Controls group together and, when space is tight, wrap as a unit (staying right-aligned). */}
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginLeft: 'auto',
            flexWrap: 'wrap',
          }}
        >
          {onDownload && (
            <button
              aria-label={translate.phrases.banyanApp('Download')}
              onClick={onDownload}
              css={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
                display: 'inline-flex',
                alignItems: 'center',
                color: dashboardColors.mutedText,
              }}
            >
              <DownloadIcon />
            </button>
          )}

          {headerActions}
        </div>
      </div>

      {/* Inset divider: the header underline is indented from the card edges (matches the design).
          Dropped once compact — the table's own header sits directly beneath it there. */}
      {!compact && <div css={{ height: 1, background: colors.grey200, margin: `0 ${HEADER_PADDING_X}px` }} />}

      <div
        css={{
          padding: BODY_PADDING,
          // The gap under the title goes when compact, so the table meets the header directly.
          paddingTop: compact ? 0 : BODY_PADDING,
          transition: 'padding-top 150ms ease',
          position: 'relative',
          ...(fillHeight ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 } : {}),
        }}
      >
        {loading && (
          <div
            css={{
              position: 'absolute',
              inset: 0,
              // Ordering in this stacking context: column headers (1) < property banners (2) <
              // this mask (4) < card header (5) < page header (10). Above the banners, which also pin
              // — at 2 the banner won on DOM order and the spinner vanished behind it. Below the card
              // header, because once that pins the body scrolls under it and `inset: 0` of the body
              // does reach it. Well below the portal layer the property dropdown renders into.
              zIndex: 4,
              background: 'rgba(255, 255, 255, 0.65)',
            }}
          >
            {/* Sticky so the spinner stays in view on tall tables, instead of centering far down the
                card (off-screen). Where this card's header pins, the spinner has to clear it AND the
                page header above it — `stickyOffset + headerHeight` is the same offset this card
                publishes to its own children. Pinning at a bare 16px puts it underneath that chrome,
                leaving only the translucent mask visible. A card that owns its own scroll region
                (fillHeight) doesn't pin and its body never travels under the chrome, so it keeps the
                plain offset. */}
            <div
              css={{
                position: 'sticky',
                top: pinHeader ? stickyOffset + headerHeight + 16 : 16,
                display: 'flex',
                justifyContent: 'center',
                padding: 16,
              }}
            >
              <Loader />
            </div>
          </div>
        )}
        <StickyOffsetContext.Provider value={stickyOffset === null ? null : stickyOffset + headerHeight}>
          {children}
        </StickyOffsetContext.Provider>
      </div>
    </div>
  )
}
